import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
// import { RouterOutlet } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule, MatFabButton } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';

import { RoomsService } from '@app/core/services/api/rooms/rooms.service';
import { Room } from '@app/core/interfaces/room';
import { TechnicianApiService } from '@app/core/services/api/user/technician/technician-api.service';
import { Technician } from '@app/core/interfaces/technician';
import { ApiResponse } from '@app/core/interfaces/response';
import { Subject, Subscription, forkJoin, timer, of, takeUntil } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';

// TODO: Define proper interface when device service is available
interface Device {
  id: string;
  name: string;
  status: 'online' | 'offline';
  lastSeen: Date;
}

@Component({
  selector: 'app-dashboard',
  standalone: true,
  imports: [
    CommonModule, 
    MatCardModule,
    MatTableModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatFabButton,
    MatButtonModule,
    MatTooltipModule,
    TranslateModule
  ],
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class DashboardComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private roomsPollingSubscription: Subscription | null = null;
  private readonly roomsPollingInterval = 15000;
  private techniciansLoaded = false;
  private roomsLoaded = false;

  // Counts for cards
  operatorCount: number = 0;
  deviceCount: number = 0;
  technicianCount: number = 0;
  
  // Table data
  rooms: Room[] = [];
  devices: Device[] = [];
  
  // Table columns
  roomsColumns: string[] = ['name', 'status', 'participants'];
  devicesColumns: string[] = ['name', 'status', 'lastSeen'];
  
  // Loading states
  isLoading: boolean = true;
  errorKey: string | null = null;

  constructor(
    private technicianService: TechnicianApiService,
    private roomsService: RoomsService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadDashboardData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.stopRoomsPolling();
    this.destroy$.complete();
  }

  private loadDashboardData(): void {
    this.isLoading = true;
    this.errorKey = null;
    this.techniciansLoaded = false;
    this.roomsLoaded = false;

    this.loadTechnicianCount();
    this.stopRoomsPolling();
    this.startRoomsPolling();

    // TODO: Load operators count - need API endpoint
    // For now, set a placeholder
    this.operatorCount = 0; // Replace with actual API call
    
    // TODO: Load devices - need API endpoint
    // For now, set some sample data
    this.devices = [];
    this.deviceCount = 0;
  }

  private loadTechnicianCount(): void {
    this.technicianService.get_technicians_list_data()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: ApiResponse<Technician[]>) => {
          this.technicianCount = response.data?.length || 0;
          this.techniciansLoaded = true;
          this.checkLoadingComplete();
        },
        error: (err) => {
          console.error('Error loading technicians:', err);
          this.errorKey = 'dashboard.error_load_technicians';
          this.techniciansLoaded = true;
          this.checkLoadingComplete();
        }
      });
  }
  
  private checkLoadingComplete(): void {
    if (this.techniciansLoaded && this.roomsLoaded) {
      this.isLoading = false;
    }
  }

  private startRoomsPolling(): void {
    if (this.roomsPollingSubscription && !this.roomsPollingSubscription.closed) {
      return;
    }

    this.roomsPollingSubscription = timer(0, this.roomsPollingInterval)
      .pipe(
        takeUntil(this.destroy$),
        switchMap(() =>
          this.fetchRoomsData().pipe(
            catchError(err => {
              console.error('Error loading rooms:', err);
              this.errorKey = 'dashboard.error_load_rooms';
              return of(null);
            })
          )
        )
      )
      .subscribe(result => {
        if (result) {
          this.rooms = this.mapRoomsWithConnections(result.rooms, result.connections);
          if (this.errorKey === 'dashboard.error_load_rooms') {
            this.errorKey = null;
          }
        }

        if (!this.roomsLoaded) {
          this.roomsLoaded = true;
          this.checkLoadingComplete();
        }
      });
  }

  private stopRoomsPolling(): void {
    if (this.roomsPollingSubscription) {
      this.roomsPollingSubscription.unsubscribe();
      this.roomsPollingSubscription = null;
    }
  }

  private fetchRoomsData() {
    return forkJoin({
      rooms: this.roomsService.getRooms(false),
      connections: this.roomsService.getRoomActive(false)
    }).pipe(
      map(({ rooms, connections }) => ({
        rooms: rooms?.data ?? [],
        connections
      }))
    );
  }

  private mapRoomsWithConnections(rooms: Room[], connections: any): Room[] {
    const connectList = Array.isArray(connections) ? connections : [];

    return rooms.map(room => {
      const connectedItem = connectList.find((item: any) => item && item['connectionId'] === room.uuid);
      return {
        ...room,
        connected: !!connectedItem,
        connectionNumber: connectedItem ? connectedItem['connectionNumber'] : 0
      };
    });
  }
  
  refreshData(): void {
    this.loadDashboardData();
  }

  canJoinRoom(room: Room): boolean {
    return this.getRoomStatus(room) === 'active' && !!room.uuid;
  }

  openRoom(room: Room): void {
    if (!this.canJoinRoom(room) || !room.uuid) {
      return;
    }
    this.router.navigate(['/support/room', room.uuid]);
  }

  getRoomStatusClass(room: Room): string {
    const status = this.getRoomStatus(room);
    switch (status) {
      case 'active':
        return 'status-badge--active';
      case 'busy':
        return 'status-badge--busy';
      default:
        return 'status-badge--inactive';
    }
  }

  getRoomStatusLabelKey(room: Room): string {
    const status = this.getRoomStatus(room);
    if (status === 'active') {
      return 'dashboard.room_status_active';
    }
    if (status === 'busy') {
      return 'dashboard.room_status_busy';
    }
    return 'dashboard.room_status_inactive';
  }

  private getRoomStatus(room: Room): 'inactive' | 'active' | 'busy' {
    if (room.connected) {
      if ((room.connectionNumber ?? 0) > 1) {
        return 'busy';
      }
      return 'active';
    }
    return 'inactive';
  }
}
